import { Box, Button, Grid } from '@mui/material';
import { t } from 'i18next';
import { useCallback, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { InferType } from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { createInvoiceAddress } from 'src/shared/services/invoiceAddressService';
import { toast } from 'react-toastify';
import { useGlobals } from 'src/shared/contexts/appContext';
import { useSchemaCreateInvoiceAddress } from '../schemas/useSchemaCreateInvoiceAddress';
import { InvoiceAddressCreateForm } from './invoiceAddressCreateForm';

type InvoiceAddressCreateModalProps = {
  modalOpen: boolean;
  onClose: () => void;
};

export const InvoiceAddressCreateModal = (props: InvoiceAddressCreateModalProps) => {
  const { modalOpen, onClose } = props;

  const isMounted = useIsMounted();

  const { setDateStamp } = useGlobals();

  const [loading, setLoading] = useState<boolean>(false);

  const addInvoiceAddressRequestSchema = useSchemaCreateInvoiceAddress();
  type AddInvoiceAddressFormInterface = InferType<typeof addInvoiceAddressRequestSchema>;

  const formMethods = useForm<AddInvoiceAddressFormInterface>({
    resolver: yupResolver(addInvoiceAddressRequestSchema),
    defaultValues: {
      name: '',
      address: '',
      postalcode: '',
      city: '',
      country: '',
      address2: '',
      email: '',
      name2: '',
    },
  });

  const { handleSubmit } = formMethods;

  const saveFormData = useCallback(
    async (data: AddInvoiceAddressFormInterface) => {
      ConsoleHelper.log('data', data);
      setLoading(true);
      try {
        await createInvoiceAddress(data);
        if (isMounted()) {
          toast.success(t('member:invoiceAddress.saveSuccess'));
          setDateStamp(new Date());
          onClose();
        }
      } catch (e) {
        if (isMounted()) ConsoleHelper.log('saveFormDataError:', e);
      } finally {
        if (isMounted()) setLoading(false);
      }
    },
    [isMounted, onClose, setDateStamp],
  );

  const onSubmit = useCallback(
    (data: AddInvoiceAddressFormInterface) => {
      saveFormData(data);
    },
    [saveFormData],
  );

  const onHandleSave = useCallback(() => {
    handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit]);

  return (
    <GenericDialog
      open={modalOpen}
      maxWidth='sm'
      onClose={() => onClose()}
      onSave={() => ConsoleHelper.log('save')}
      dialogTitle={t('member:invoiceAddress.createModalHeader')}
      padding={0}
      dialogContent={
        <Box
          sx={{
            position: 'relative',
            minHeight: '20rem',
          }}
        >
          <LoadingOverlay loading={loading} />
          <Box sx={{ p: 2 }}>
            <GridWrapper>
              <Grid item xs={12}>
                <FormProvider {...formMethods}>
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <Grid container spacing={2} p={2}>
                      <InvoiceAddressCreateForm />
                    </Grid>
                  </form>
                </FormProvider>
              </Grid>
            </GridWrapper>
          </Box>
        </Box>
      }
      dialogActions={
        <Box display='flex' flexDirection='row' justifyContent='flex-end' width='100%'>
          <Button disableElevation variant='contained' color='primary' onClick={onHandleSave} disabled={loading}>
            {t('common:button.save')}
          </Button>
        </Box>
      }
    />
  );
};
